import { useState } from 'react';
import { motion } from 'framer-motion';
import { useSEO } from '../../hooks/useSEO';
import { generateContactPageSchema, generateBreadcrumbSchema } from '../../utils/seo';
import { trackFormSubmit } from '../../utils/analytics';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Breadcrumb from '../../components/base/Breadcrumb';
import { createWhatsAppLink } from '../../lib/whatsapp';

export default function ContatoPage() {
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    assunto: '',
    mensagem: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // SEO
  useSEO({
    title: 'Contato - Mateus Oliveira Corretor | Aracaju e Barra dos Coqueiros',
    description: 'Entre em contato com Mateus Oliveira para comprar, vender ou alugar imóveis em Aracaju e Barra dos Coqueiros. WhatsApp (79) 99988-1002. Atendimento rápido e personalizado.',
    keywords: 'contato mateus oliveira, whatsapp corretor aracaju, falar com corretor sergipe, contato imobiliária aracaju',
    canonical: window.location.origin + '/contato',
    schema: [
      generateContactPageSchema(),
      generateBreadcrumbSchema([{ name: 'Contato', url: '/contato' }]),
    ],
  });

  const whatsappLink = createWhatsAppLink('Olá! Gostaria de mais informações sobre os imóveis.');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Validação
      if (!formData.nome || !formData.telefone || !formData.email || !formData.assunto || !formData.mensagem) {
        setError('Por favor, preencha todos os campos obrigatórios');
        setLoading(false);
        return;
      }

      // Criar lead no CRM
      const { error: leadError } = await supabase
        .from('leads')
        .insert([{
          name: formData.nome,
          phone: formData.telefone,
          email: formData.email,
          origem: 'Site - Formulário de Contato',
          interesse: formData.assunto.toLowerCase().includes('comprar') ? 'compra' : 
                     formData.assunto.toLowerCase().includes('alugar') ? 'aluguel' : 'compra',
          observacoes: `Assunto: ${formData.assunto}\n\nMensagem: ${formData.mensagem}`,
          etapa: 'LEAD',
          prioridade: 'media',
          status: 'ativo',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);

      if (leadError) throw leadError;

      // Enviar para o formulário também
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('nome', formData.nome);
      formDataToSend.append('telefone', formData.telefone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('assunto', formData.assunto);
      formDataToSend.append('mensagem', formData.mensagem);

      await fetch('https://readdy.ai/api/form/d5sfncg6qf5t6pbjhmp0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      trackFormSubmit('contato');
      setSuccess(true);
      setFormData({
        nome: '',
        telefone: '',
        email: '',
        assunto: '',
        mensagem: ''
      });

      // Limpar mensagem de sucesso após 5 segundos
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      console.error('Erro ao enviar formulário:', err);
      setError('Erro ao enviar mensagem. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={[{ name: 'Contato', url: '/contato' }]} />

          <div className="pt-24 pb-16">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h1 className="text-6xl font-bold text-gray-900 mb-6 font-serif">
                  Entre em Contato
                </h1>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Estamos prontos para ajudar você a encontrar o imóvel ideal em Aracaju e Barra dos Coqueiros
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                {/* Formulário */}
                <div>
                  <div className="bg-gray-50 rounded-2xl p-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 font-serif">
                      Envie sua Mensagem
                    </h2>

                    {success && (
                      <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm">
                        ✅ Mensagem enviada com sucesso! Entraremos em contato em breve.
                      </div>
                    )}

                    {error && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                        {error}
                      </div>
                    )}

                    <form
                      data-readdy-form
                      id="contato-lead"
                      onSubmit={handleSubmit}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nome Completo *
                        </label>
                        <input
                          type="text"
                          name="nome"
                          value={formData.nome}
                          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Telefone *
                          </label>
                          <input
                            type="tel"
                            name="telefone"
                            value={formData.telefone}
                            onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                            required
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            E-mail *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Assunto *
                        </label>
                        <select
                          name="assunto"
                          value={formData.assunto}
                          onChange={(e) => setFormData({ ...formData, assunto: e.target.value })}
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="">Selecione um assunto</option>
                          <option value="Comprar Imóvel">Comprar Imóvel</option>
                          <option value="Vender Imóvel">Vender Imóvel</option>
                          <option value="Alugar Imóvel">Alugar Imóvel</option>
                          <option value="Informações Gerais">Informações Gerais</option>
                          <option value="Outro">Outro</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Mensagem *
                        </label>
                        <textarea
                          name="mensagem"
                          value={formData.mensagem}
                          onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                          rows={6}
                          maxLength={500}
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        ></textarea>
                        <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-orange-500 text-white py-4 rounded-lg hover:bg-orange-600 transition-colors font-medium whitespace-nowrap disabled:opacity-50"
                      >
                        {loading ? 'Enviando...' : 'Enviar Mensagem'}
                      </button>
                    </form>
                  </div>
                </div>

                {/* Informações de Contato */}
                <div>
                  <div className="space-y-6 mb-8">
                    <div className="bg-gray-50 rounded-2xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-500 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-whatsapp-line text-2xl"></i>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 mb-2">WhatsApp</h3>
                          <p className="text-gray-600 mb-3">
                            Fale conosco diretamente pelo WhatsApp
                          </p>
                          <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 hover:underline font-medium whitespace-nowrap"
                          >
                            (79) 99988-1002
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-500 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-mail-line text-2xl"></i>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 mb-2">E-mail</h3>
                          <p className="text-gray-600 mb-3">
                            Envie um e-mail para nós
                          </p>
                          <a
                            href="mailto:contato@mateusimoveisse.com.br"
                            className="text-orange-600 hover:underline font-medium"
                          >
                            contato@mateusimoveisse.com.br
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-500 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-line text-2xl"></i>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 mb-2">Endereço</h3>
                          <p className="text-gray-600">
                            Aracaju e Barra dos Coqueiros<br />
                            Sergipe - Brasil
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-500 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-time-line text-2xl"></i>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 mb-2">Horário de Atendimento</h3>
                          <p className="text-gray-600">
                            Segunda a Sexta: 8h às 18h<br />
                            Sábado: 8h às 12h
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mapa */}
                  <div className="rounded-2xl overflow-hidden shadow-lg" style={{ height: '300px' }}>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d125483.89076586!2d-37.1431!3d-10.9472!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x71ab1c0f89e0c77%3A0x2d35e1c5b3c6c5c5!2sAracaju%2C%20SE!5e0!3m2!1spt-BR!2sbr!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <WhatsAppButton />
      <Footer />
    </>
  );
}
